/**
 * Google Ads Frontend API Client
 * Calls backend API instead of using google-ads-api directly
 * This resolves Vite build errors by moving Node.js dependencies to backend
 */

// API base URL - will use environment variable or default to production URL
const API_BASE_URL = import.meta.env.VITE_GOOGLE_ADS_API_URL || 'https://marketing.rightawayhome.com';

function addCompanyToBody(body: Record<string, any>, company?: string) {
  return company ? { ...body, company } : body;
}

function appendCompanyToParams(params: URLSearchParams, company?: string) {
  if (company) {
    params.append('company', company);
  }
}

// ============================================================================
// Types & Interfaces (kept for compatibility)
// ============================================================================

export interface GoogleAdsAccount {
  customerId: string;
  descriptiveName: string;
  currencyCode: string;
  timeZone: string;
  isManager: boolean;
  status: string;
}

export interface Campaign {
  id: string;
  name: string;
  status: string;
  advertisingChannelType: string;
  biddingStrategyType: string;
  budget: {
    amountMicros: number;
    deliveryMethod: string;
  };
  targetSpend?: number;
  targetCpa?: number;
  targetRoas?: number;
}

export interface CampaignMetrics {
  campaignId: string;
  campaignName: string;
  date: string;
  impressions: number;
  clicks: number;
  costMicros: number;
  conversions: number;
  conversionValue: number;
  ctr: number;
  avgCpcMicros: number;
  avgCpmMicros: number;
}

export interface KeywordMetrics {
  keywordId: string;
  keywordText: string;
  matchType: string;
  adGroupId: string;
  adGroupName: string;
  campaignId: string;
  campaignName: string;
  impressions: number;
  clicks: number;
  costMicros: number;
  conversions: number;
  conversionValue: number;
  ctr: number;
  avgCpcMicros: number;
  qualityScore?: number;
  cpcBidMicros: number;
  firstPageCpcMicros?: number;
  topOfPageCpcMicros?: number;
}

export interface KeywordIdea {
  keyword: string;
  avgMonthlySearches: number;
  competition: 'LOW' | 'MEDIUM' | 'HIGH';
  competitionIndex: number;
  lowTopOfPageBidMicros: number;
  highTopOfPageBidMicros: number;
}

export interface SearchTerm {
  searchTerm: string;
  addedAsKeyword: boolean;
  impressions: number;
  clicks: number;
  costMicros: number;
  conversions: number;
  ctr: number;
  avgCpcMicros: number;
}

export interface ConversionAction {
  id: string;
  name: string;
  category: string;
  status: string;
  type: string;
  countingType: string;
}

export interface QualityScoreData {
  keywordId: string;
  keywordText: string;
  qualityScore: number;
  creativeQualityScore: string;
  landingPageExperienceQualityScore: string;
  searchPredictedCtr: string;
}

// ============================================================================
// API Helper Functions
// ============================================================================

async function apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Check if response is successful
  if (!response.ok) {
    // Try to parse error message from JSON, fallback to status text
    try {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || `API Error: ${response.status} ${response.statusText}`);
    } catch (e) {
      if (e instanceof Error && e.message.startsWith('API Error')) throw e;
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'API call failed');
  }

  return data.data as T;
}

// ============================================================================
// Google Ads API Client Class
// ============================================================================

export class GoogleAdsClient {
  constructor(config?: any) {
    // Config is handled by backend, not needed on frontend
  }

  /**
   * Get all accessible Google Ads accounts
   */
  async getAccessibleAccounts(): Promise<GoogleAdsAccount[]> {
    return apiCall<GoogleAdsAccount[]>('/api/google-ads/accounts');
  }

  /**
   * List all campaigns for a customer
   */
  async listCampaigns(
    customerId: string,
    filters?: { status?: string; type?: string; company?: string },
    company?: string
  ): Promise<Campaign[]> {
    const params = new URLSearchParams({ customerId });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    appendCompanyToParams(params, filters?.company || company);

    return apiCall<Campaign[]>(`/api/google-ads/campaigns?${params}`);
  }

  /**
   * Get campaign performance metrics for a date range
   */
  async getCampaignMetrics(
    customerId: string,
    campaignIds: string[],
    dateRange: { startDate: string; endDate: string },
    company?: string
  ): Promise<CampaignMetrics[]> {
    return apiCall<CampaignMetrics[]>('/api/google-ads/campaigns/metrics', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        campaignIds,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }, company)),
    });
  }

  /**
   * Get keyword performance metrics
   */
  async getKeywordMetrics(
    customerId: string,
    filters: {
      campaignId?: string;
      adGroupId?: string;
      dateRange: { startDate: string; endDate: string };
      company?: string;
    },
    company?: string
  ): Promise<KeywordMetrics[]> {
    return apiCall<KeywordMetrics[]>('/api/google-ads/keywords/metrics', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        campaignId: filters.campaignId,
        adGroupId: filters.adGroupId,
        startDate: filters.dateRange.startDate,
        endDate: filters.dateRange.endDate,
      }, filters.company || company)),
    });
  }

  /**
   * Get search term report (actual user queries)
   */
  async getSearchTerms(
    customerId: string,
    filters: {
      campaignId?: string;
      dateRange: { startDate: string; endDate: string };
      company?: string;
    },
    company?: string
  ): Promise<SearchTerm[]> {
    return apiCall<SearchTerm[]>('/api/google-ads/search-terms', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        campaignId: filters.campaignId,
        startDate: filters.dateRange.startDate,
        endDate: filters.dateRange.endDate,
      }, filters.company || company)),
    });
  }

  /**
   * Collect and persist keyword metrics to Supabase (server-side)
   */
  async collectKeywordMetrics(
    customerId: string,
    payload: {
      campaignId?: string;
      adGroupId?: string;
      dateRange: { startDate: string; endDate: string };
      geoTargetConstants?: string[];
      company?: string;
    },
    company?: string
  ): Promise<{ rowsInserted: number }> {
    return apiCall<{ rowsInserted: number }>('/api/google-ads/collect/keywords', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        campaignId: payload.campaignId,
        adGroupId: payload.adGroupId,
        startDate: payload.dateRange.startDate,
        endDate: payload.dateRange.endDate,
        geoTargetConstants: payload.geoTargetConstants,
      }, payload.company || company)),
    });
  }

  /**
   * Collect and persist search terms to Supabase (server-side)
   */
  async collectSearchTerms(
    customerId: string,
    payload: {
      campaignId?: string;
      dateRange: { startDate: string; endDate: string };
      company?: string;
    },
    company?: string
  ): Promise<{ rowsInserted: number }> {
    return apiCall<{ rowsInserted: number }>('/api/google-ads/collect/search-terms', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        campaignId: payload.campaignId,
        startDate: payload.dateRange.startDate,
        endDate: payload.dateRange.endDate,
      }, payload.company || company)),
    });
  }

  /**
   * Update campaign budget
   */
  async updateCampaignBudget(
    customerId: string,
    campaignId: string,
    newBudgetMicros: number,
    company?: string
  ): Promise<void> {
    await apiCall(`/api/google-ads/campaigns/${campaignId}/budget`, {
      method: 'PUT',
      body: JSON.stringify(addCompanyToBody({ customerId, newBudgetMicros }, company)),
    });
  }

  /**
   * Pause a campaign
   */
  async pauseCampaign(customerId: string, campaignId: string, company?: string): Promise<void> {
    await apiCall(`/api/google-ads/campaigns/${campaignId}/pause`, {
      method: 'PUT',
      body: JSON.stringify(addCompanyToBody({ customerId }, company)),
    });
  }

  /**
   * Resume a campaign
   */
  async resumeCampaign(customerId: string, campaignId: string, company?: string): Promise<void> {
    await apiCall(`/api/google-ads/campaigns/${campaignId}/resume`, {
      method: 'PUT',
      body: JSON.stringify(addCompanyToBody({ customerId }, company)),
    });
  }

  /**
   * Update keyword bids (batch operation)
   */
  async updateKeywordBids(
    customerId: string,
    keywordBids: { keywordId: string; adGroupId: string; newBidMicros: number }[]
    ,
    company?: string
  ): Promise<void> {
    await apiCall('/api/google-ads/keywords/bids', {
      method: 'PUT',
      body: JSON.stringify(addCompanyToBody({ customerId, keywordBids }, company)),
    });
  }

  /**
   * Generate keyword ideas using Keyword Planner
   */
  async generateKeywordIdeas(
    customerId: string,
    seedKeywords: string[],
    geoTargetConstants: string[] = ['geoTargetConstants/2840'],
    languageConstant: string = 'languageConstants/1000',
    company?: string
  ): Promise<KeywordIdea[]> {
    return apiCall<KeywordIdea[]>('/api/google-ads/keywords/ideas', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        seedKeywords,
        geoTargetConstants,
        languageConstant,
      }, company)),
    });
  }

  /**
   * Get keyword statistics for specific keywords
   */
  async getKeywordStats(
    customerId: string,
    keywords: string[],
    geoTargetConstants: string[] = ['geoTargetConstants/2840'],
    company?: string
  ): Promise<KeywordIdea[]> {
    return apiCall<KeywordIdea[]>('/api/google-ads/keywords/stats', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        keywords,
        geoTargetConstants,
      }, company)),
    });
  }

  /**
   * List all conversion actions
   */
  async listConversionActions(customerId: string, company?: string): Promise<ConversionAction[]> {
    const params = new URLSearchParams({ customerId });
    appendCompanyToParams(params, company);
    return apiCall<ConversionAction[]>(`/api/google-ads/conversions/actions?${params}`);
  }

  /**
   * Get conversion metrics by campaign
   */
  async getConversionMetrics(
    customerId: string,
    dateRange: { startDate: string; endDate: string },
    campaignId?: string,
    company?: string
  ): Promise<any[]> {
    return apiCall<any[]>('/api/google-ads/conversions/metrics', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        campaignId,
      }, company)),
    });
  }

  /**
   * Get quality scores for keywords
   */
  async getQualityScores(
    customerId: string,
    filters: {
      campaignId?: string;
      adGroupId?: string;
      company?: string;
    },
    company?: string
  ): Promise<QualityScoreData[]> {
    return apiCall<QualityScoreData[]>('/api/google-ads/quality-scores', {
      method: 'POST',
      body: JSON.stringify(addCompanyToBody({
        customerId,
        campaignId: filters.campaignId,
        adGroupId: filters.adGroupId,
      }, filters.company || company)),
    });
  }

  /**
   * Convert micros to currency
   */
  static microsToCurrency(micros: number): number {
    return micros / 1000000;
  }

  /**
   * Convert currency to micros
   */
  static currencyToMicros(amount: number): number {
    return Math.round(amount * 1000000);
  }

  /**
   * Format currency from micros
   */
  static formatCurrency(micros: number, currencyCode: string = 'USD'): string {
    const amount = GoogleAdsClient.microsToCurrency(micros);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  }
}

// ============================================================================
// Singleton Instance Factory
// ============================================================================

let clientInstance: GoogleAdsClient | null = null;

/**
 * Get or create Google Ads client instance
 */
export function getGoogleAdsClient(customerId?: string): GoogleAdsClient {
  if (!clientInstance) {
    clientInstance = new GoogleAdsClient();
  }

  return clientInstance;
}

/**
 * Helper to get date range strings
 */
export function getDateRange(days: number = 30): {
  startDate: string;
  endDate: string;
} {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}

/**
 * Florida geo target constants for Google Ads
 */
export const FLORIDA_GEO_TARGETS = {
  FLORIDA: 'geoTargetConstants/21137',
  TAMPA: 'geoTargetConstants/1026139',
  MIAMI: 'geoTargetConstants/1015168',
  FORT_LAUDERDALE: 'geoTargetConstants/1022178',
};
